<script setup lang="ts">
import AddMenuCourseIcon from '@/assets/icons/Planning/AddMenuCourseIcon.vue';
import { usePermissions } from '@/composables/usePermissions';
import type { MessageSchema } from '@/i18n/index.js';
import { useDishesStore } from '@/stores/dishesStore';
import { usePlanningStore } from '@/stores/planningStore';
import { useUserStore } from '@/stores/useUserStore';
import type { Dish } from '@/types/dish.js';
import type { MenuCourse } from '@/types/planning';
import Button from 'primevue/button';
import Dialog from 'primevue/dialog';
import InputText from 'primevue/inputtext';
import Menu from 'primevue/menu';
import { useConfirm } from 'primevue/useconfirm';
// import { useToast } from 'primevue/usetoast';
import { computed, ref, type Ref } from 'vue';
import { useI18n } from 'vue-i18n';
// import { useRoute, useRouter } from 'vue-router';
import GenerateDishFromDialog from './GenerateDishFromDialog.vue';
import MenuCourseInfoDialog from './MenuCourseInfoDialog.vue';
import PlanDishFromDialog from './PlanDishFromDialog.vue';

const { t } = useI18n<{ message: MessageSchema }>({ useScope: 'global' });
const { can } = usePermissions();
// const route = useRoute();
// const router = useRouter();
const popupMenu = ref();
const openMenuCourseModal = ref(false);
const confirmDialog = useConfirm();
const { user } = useUserStore();
// const toast = useToast();
const isLoading = ref(false);

const planningStore = ref(usePlanningStore());
const showPlanDishFromDialog: Ref<boolean> = ref(false);
const showGenerateDishFromDialog: Ref<boolean> = ref(false);
const dialogCategory: Ref<'history' | 'favorites'> = ref('history');

const dishesStore = ref(useDishesStore());

const showMenuCourseInfoDialog = ref(false);
// Function to temporarily add the clipboard dish
// function addTemporaryDish() {
//   if (!planningStore.dishToPlanClipboard) return;
//   const dish = planningStore.dishToPlanClipboard;
//   emit('addOrUpdateMenuCourse', { id: -1, name: dish.name, order: props.order, dish: dish, planned_menu_id: -1 });
// }

const props = defineProps<{
  order: 1 | 2 | 3;
  menuCourse: MenuCourse | null;
}>();

const newMenuCourse = ref<MenuCourse>({
  id: -1,
  name: '',
  order: props.order,
  planned_menu_id: -1,
  dish: null,
});

const emit = defineEmits<{
  (event: 'addOrUpdateMenuCourse', newMenuCourse: MenuCourse);
  (event: 'deleteMenuCourse', menuCourseId: number);
}>();

const label = computed(() => {
  switch (props.order) {
    case 1:
      return t('planning.plannedMenu.starter');
    case 2:
      return t('planning.plannedMenu.mainDish');
    case 3:
      return t('planning.plannedMenu.dessert');
    default:
      return '';
  }
});

// const redirectToDishDetail = () => {
//   if (!props.menuCourse?.dish) return;
//   const dishId = props.menuCourse?.dish.id; // Assuming dish has an id property
//   router.push(`/menu/dishes/${dishId}`);
// };

const items = computed(() => {
  const menuItems: Array<{
    label: string;
    icon: string;
    command: () => void | Promise<void>;
  }> = [];

  // Generate dish action - requires dishes_create permission
  if (can('dishes', 'create')) {
    menuItems.push({
      label: t('planning.plannedMenu.generateDish'),
      icon: 'pi pi-arrow-up-right',
      command: () => generateDishFromDialog(),
    });
  }

  // Plan from recipes - requires planning_create permission
  if (can('planning', 'create')) {
    menuItems.push({
      label: t('planning.plannedMenu.myRecipes'),
      icon: 'pi pi-book',
      command: () => planDishFromPlanning(),
    });
  }

  // Write dish action - requires planning_create permission
  if (can('planning', 'create')) {
    menuItems.push({
      label: t('planning.plannedMenu.writeDish'),
      icon: 'pi pi-pencil',
      command: () => {
        if (props.menuCourse) {
          newMenuCourse.value = {
            id: props.menuCourse.id,
            name: props.menuCourse.name ?? null,
            order: props.order,
            dish: props.menuCourse.dish ?? null,
            planned_menu_id: props.menuCourse.planned_menu_id,
          };
        } else {
          newMenuCourse.value = {
            id: -1,
            name: '',
            order: props.order,
            planned_menu_id: -1,
            dish: null,
          };
        }
        openMenuCourseModal.value = true;
      },
    });
  }

  return menuItems.length > 0 ? [{ items: menuItems }] : [];
});

function togglePopUpMenu(event: MouseEvent) {
  // Only allow opening menu if user has planning permissions
  if (!can('planning', 'create') && !can('dishes', 'create')) return;
  popupMenu.value.toggle(event);
}

// Computed property to control visibility of the add button and menu
const canAddMenuCourse = computed(() => {
  return can('planning', 'create') || can('dishes', 'create');
});

// Computed property to control visibility of delete action
const canDeleteMenuCourse = computed(() => {
  return can('planning', 'delete');
});

function addOrUpdateMenuCourse() {
  // Check permission before adding/updating
  if (!can('planning', 'create')) return;
  if (planningStore.value.dishToPlan) {
    newMenuCourse.value = {
      id: -1,
      name: planningStore.value.dishToPlan.clipboardDish.name,
      order: props.order,
      planned_menu_id: -1,
      dish: planningStore.value.dishToPlan.clipboardDish,
    };
    // console.log("MenuCourse add clipboard", newMenuCourse.value)
    // planningStore.value.dishToPlanClipboard = null;
  } else if (newMenuCourse.value.id === -1) {
    // console.log("MenuCourse create", newMenuCourse.value)
  } else {
    // console.log("MenuCourse update", newMenuCourse.value)
    // emit('addOrUpdateMenuCourse', newMenuCourse.value);
    // Handle update logic here
  }
  emit('addOrUpdateMenuCourse', newMenuCourse.value);
  openMenuCourseModal.value = false;
}

function menuCourseModal(menuCourse: MenuCourse | null) {
  if (menuCourse) {
    newMenuCourse.value = {
      id: menuCourse.id,
      name: menuCourse.name ?? null,
      order: props.order,
      dish: menuCourse.dish ?? null,
      planned_menu_id: menuCourse.planned_menu_id,
    };
  } else {
    newMenuCourse.value = {
      id: -1,
      name: '',
      order: props.order,
      planned_menu_id: -1,
      dish: null,
    };
  }
  openMenuCourseModal.value = !openMenuCourseModal.value;
}

function deleteMenuCourse() {
  // Check permission before deleting
  if (!can('planning', 'delete')) return;
  if (!props.menuCourse) return;
  // console.log("MenuCourse delete", newMenuCourse.value)
  emit('deleteMenuCourse', props.menuCourse.id);
}

function handleClickOnMenuCourse() {
  if (!props.menuCourse || planningStore.value.dishToPlan || isLoading.value) return;
  if (props.menuCourse.dish) {
    dishesStore.value.showingDishFromPlan = true;
    showMenuCourseInfoDialog.value = true;
  } else {
    menuCourseModal(props.menuCourse);
  }
}

const confirmDeleteMenuCourse = () => {
  // Check permission before showing delete confirmation
  if (!can('planning', 'delete')) return;

  confirmDialog.require({
    message: t('planning.plannedMenu.menuCourse.delete.message'),
    header: t('planning.plannedMenu.menuCourse.delete.title'),
    icon: 'pi pi-exclamation-triangle',
    rejectClass: 'p-button-secondary p-button-outlined',
    rejectLabel: t('planning.plannedMenu.menuCourse.delete.cancel'),
    acceptLabel: t('planning.plannedMenu.menuCourse.delete.confirm'),
    accept: () => {
      deleteMenuCourse();
    },
    reject: () => {},
  });
};
// async function planDishFromPlanning(category?: 'history' | 'favorites') {
async function planDishFromPlanning() {
  // Check permission before planning dish
  if (!can('planning', 'create')) return;
  // if (!dish.value) return

  planningStore.value.choosingDishToPlan = true;
  // dialogCategory.value = category
  showPlanDishFromDialog.value = true;
}

async function generateDishFromDialog() {
  // Check permission before generating dish
  if (!can('dishes', 'create')) return;
  // if (!dish.value) return

  planningStore.value.choosingDishToPlan = true;
  // dialogCategory.value = category
  showGenerateDishFromDialog.value = true;
}

async function addDishToPlan(dish: Dish) {
  // Check permission before adding dish to plan
  if (!can('planning', 'create')) return;
  if (!dish) return;
  newMenuCourse.value = {
    id: -1,
    name: dish.name,
    order: props.order,
    planned_menu_id: -1,
    dish: dish,
  };
  showPlanDishFromDialog.value = false;
  planningStore.value.choosingDishToPlan = false;
  addOrUpdateMenuCourse();
}

async function addDishTitleToPlan(dishTitle: Dish) {
  // Check permission before adding dish title to plan
  if (!can('planning', 'create')) return;
  // TODO : Add the id info even if it's not a dish but a dish title
  newMenuCourse.value = {
    id: -1,
    name: dishTitle.name,
    order: props.order,
    planned_menu_id: -1,
    dish: null,
  };
  showPlanDishFromDialog.value = false;
  planningStore.value.choosingDishToPlan = false;
  addOrUpdateMenuCourse();
}

async function generateDishFromTitle() {
  // Check permission before generating dish from title
  if (!can('dishes', 'create')) return;
  if (!user) return;
  if (!useUserStore().validateUserCanGenerate()) {
    return null;
  }
  const title = newMenuCourse.value.name; // Le titre est dans newMenuCourse
  const restaurantId = user.restaurants[0].id;
  if (!title) {
    console.error('Title is required to generate a dish');
    return;
  }
  isLoading.value = true;
  openMenuCourseModal.value = false;

  try {
    const dish = await dishesStore.value.generateDishFromTitle(title, restaurantId);
    // console.log("Dish GENERATED ", dish)
    if (!dish || !props.menuCourse) {
      isLoading.value = false;
      return;
    }

    // let dishType: 'starter' | 'main' | 'dessert' = 'main';
    // switch (props.menuCourse.order) {
    //   case 1:
    //     dishType = 'starter';
    //     break;
    //   case 2:
    //     dishType = 'main';
    //     break;
    //   case 3:
    //     dishType = 'dessert';
    //     break;
    //   default:
    //     'main';
    //     break;
    // }
    newMenuCourse.value.dish = dish;
    // const data = await planningStore.value.planDish(dish, [props.menuCourse.planned_menu_id], dishType);
    // console.log("DATA ", data)
    if (dish) {
      // Mettre à jour le plat dans le composant
      newMenuCourse.value.dish = dish;
      // console.log('Dish generated and updated:', dish);
    } else {
      console.error('Failed to generate dish');
    }
    addOrUpdateMenuCourse();
    isLoading.value = false;
  } catch (error) {
    console.error('Error generating dish from title:', error);
  }
  isLoading.value = false;
}

// watch(() => props.menuCourse, (newValue) => {
//   console.log("Changing menuCourse", newValue)
// });
</script>

<template>
  <div class="flex flex-col gap-4" :class="{ 'pointer-events-none opacity-50': isLoading }">
    <div class="grid grid-cols-12 items-center gap-2">
      <div class="col-span-3 min-w-4">
        <p>{{ label }}</p>
      </div>
      <!-- <div
        class="cursor-pointer col-span-9 relative bg-primary-200 py-6 px-4 rounded-xl shadow-lg min-w-52 flex items-center"
        @click="redirectToDishDetail">
        <p class="text-sm line-clamp-2">{{ props.menuCourse.dish.name }}</p>
      </div> -->
      <div v-if="menuCourse" class="gaps-2 col-span-9 grid grid-cols-9 items-center">
        <div
          class="relative col-span-8 flex min-w-52 cursor-pointer items-center rounded-xl bg-primary-100 px-4 py-6 shadow-clickable-card"
          @click="handleClickOnMenuCourse"
        >
          <div v-if="isLoading" class="flex items-center gap-2 text-sm">
            <i class="pi pi-spin pi-spinner text-primary-500" style="font-size: 1rem"></i>
            {{ t('common.generating') }} {{ menuCourse.dish?.name ?? menuCourse.name }}
          </div>
          <div v-else class="flex w-full items-center justify-between">
            <p class="line-clamp-2 font-semibold text-sm">
              {{ menuCourse.dish?.name ?? menuCourse.name }}
            </p>
            <i class="pi pi-angle-right text-lg"></i>
          </div>
        </div>
        <div
          v-if="!planningStore.dishToPlan && canDeleteMenuCourse"
          class="col-span-1 flex cursor-pointer justify-end p-2 text-right"
          @click="confirmDeleteMenuCourse"
        >
          <i class="pi pi-times"></i>
        </div>
      </div>
      <div v-else-if="canAddMenuCourse" class="col-span-7 flex cursor-pointer items-center justify-center px-4 py-6">
        <button
          class="flex aspect-square w-10 items-center justify-center rounded-full bg-accent p-2 text-white shadow-md"
          @click="planningStore.dishToPlan ? addOrUpdateMenuCourse() : togglePopUpMenu($event)"
        >
          <!-- Icon plus -->
          <AddMenuCourseIcon v-if="planningStore.dishToPlan" class="text-white" />
          <i v-else class="pi pi-plus" style="font-size: 1rem"></i>
        </button>
        <!-- <Button link rounded :pt="{ root: { class: '!p-1.5 !w-8 !h-8 rotate-90' } }" :pt-options="{ mergeProps: true }"
          @click="togglePopUpMenu">
          <template #icon>
            <ButtonMenu />
          </template>
</Button> -->
        <Menu
          id="overlay_menu"
          ref="popupMenu"
          :model="items"
          :popup="true"
          :pt="{ submenuHeader: { class: '!p-0' } }"
          :pt-options="{ mergeProps: true }"
        />
      </div>
    </div>
  </div>
  <div class="card flex justify-center" :class="{ 'pointer-events-none opacity-50': isLoading }">
    <!-- <Button label="Show" @click="openMenuCourseModal = true" /> -->
    <Dialog
      v-model:visible="openMenuCourseModal"
      modal
      :header="t('planning.plannedMenu.addDishTitle')"
      :style="{ width: '25rem', maxWidth: '90vw' }"
    >
      <div v-if="newMenuCourse" class="mb-4 flex items-center gap-4">
        <label for="dishTitle" class="w-24 font-semibold">{{ t('planning.plannedMenu.nameOfTheDish') }}</label>
        <InputText id="dishTitle" v-model="newMenuCourse.name" class="flex-auto" autocomplete="off" />
      </div>
      <div class="flex justify-end gap-4">
        <!-- <Button type="button" :label="t('common.cancel')" outlined @click="menuCourseModal(null)"
            :disabled="isLoading"></Button> -->
        <div class="flex justify-end gap-4">
          <Button
            v-if="!(isLoading || !newMenuCourse?.name || newMenuCourse.id == -1) && can('dishes', 'create')"
            type="button"
            :label="t('common.generate')"
            severity="secondary"
            :disabled="isLoading || !newMenuCourse?.name || newMenuCourse.id == -1"
            @click="generateDishFromTitle"
          ></Button>
          <Button type="button" :label="t('common.save')" :disabled="isLoading || !can('planning', 'create')" @click="addOrUpdateMenuCourse"></Button>
        </div>
      </div>
    </Dialog>
  </div>
  <PlanDishFromDialog
    v-model:visible="showPlanDishFromDialog"
    :planned-menu-name="planningStore.selectedPlannedMenu?.name ?? ''"
    :category="dialogCategory"
    @add-dish-to-plan="addDishToPlan"
    @add-dish-title-to-plan="addDishTitleToPlan"
  />
  <GenerateDishFromDialog
    v-model:visible="showGenerateDishFromDialog"
    :planned-menu-name="planningStore.selectedPlannedMenu?.name ?? ''"
    :category="dialogCategory"
    @add-dish-to-plan="addDishToPlan"
    @add-dish-title-to-plan="addDishTitleToPlan"
  />
  <MenuCourseInfoDialog
    v-if="menuCourse?.dish"
    v-model:visible="showMenuCourseInfoDialog"
    :planned-menu-name="planningStore.selectedPlannedMenu?.name ?? ''"
    :menu-course="menuCourse"
  />
</template>

<style scoped></style>
